import React, { FC, MouseEvent, Suspense, useCallback } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { useAnalytics } from '@funtech-int/yango-shared';
import { useInView } from 'react-intersection-observer';

import { Button } from '@/shared/components/Button/Button';
import { RouterLink } from '@/shared/components/RouterLink/RouterLink';
import { mainRoute } from '@/scenarios/home/routes';
import { useSearchParams } from '@/shared/hooks/useSearchParams';
import { QueryParameter } from '@/shared/constants/queryParams';
import { ProfileListConfig } from '@/scenarios/profiles/hooks/useProfileList';
import { ProfilesAnnouncementBackgroundYango } from '@/scenarios/profiles/components/ProfilesAnnouncementBackgroundYango/ProfilesAnnouncementBackgroundYango';
import { useI18n } from '@/scenarios/profiles/hooks/useI18n';

import { ProfilesFamilyVerticalList } from '../ProfilesFamilyVerticalList/ProfilesFamilyVerticalList';
import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';

import styles from './ProfilesMovieAnnouncePage.module.css';

const EMPTY_FALLBACK = () => null;

interface ProfileMovieAnnouncePageProps
    extends Omit<ProfileListConfig, 'isProfilesPageAutoShowed'> {
    onHideProfilesChoice: () => void;
}

export const ProfilesMovieAnnouncePage: FC<ProfileMovieAnnouncePageProps> = ({
    profileOttId,
    profileDisplayName,
    profileImage,
    profileAgeRestriction,
    profileHasSubscription,
    onHideProfilesChoice,
    onChooseParentAccount,
}) => {
    const i18n = useI18n();
    const searchParams = useSearchParams();
    const isProfilesPageAutoShowed = Boolean(searchParams.has(QueryParameter.WELCOME_PAGE));
    const analytics = useAnalytics(createAnalyticsEvents);

    const { ref: setElementRef } = useInView({
        triggerOnce: true,
        threshold: 0.01,
        onChange: (inView) => {
            if (inView) {
                analytics.showed(isProfilesPageAutoShowed);
            }
        },
    });

    const handleProfilesChoice = useCallback(
        (e: MouseEvent) => {
            e.preventDefault();
            analytics.askNoMore();
            onHideProfilesChoice();
        },
        [analytics, onHideProfilesChoice]
    );

    return (
        <section className={styles.root} ref={setElementRef}>
            <div className={styles.fade} />
            <div className={styles.content}>
                <ErrorBoundary fallback={EMPTY_FALLBACK}>
                    <Suspense fallback={null}>
                        <h2 className={styles.title}>
                            {i18n('profiles_movie_announce_page__choose')}
                        </h2>

                        <ProfilesFamilyVerticalList
                            profileOttId={profileOttId}
                            profileDisplayName={profileDisplayName}
                            profileHasSubscription={profileHasSubscription}
                            profileImage={profileImage}
                            profileAgeRestriction={profileAgeRestriction}
                            isProfilesPageAutoShowed={isProfilesPageAutoShowed}
                            onChooseParentAccount={onChooseParentAccount}
                        />

                        <RouterLink
                            to={mainRoute()}
                            className={styles.link}
                            onClick={handleProfilesChoice}
                        >
                            <Button theme="dark" variant="secondary" size={52}>
                                {i18n('profiles_movie_announce_page__not_again')}
                            </Button>
                        </RouterLink>
                    </Suspense>
                </ErrorBoundary>
            </div>
            <ErrorBoundary fallback={EMPTY_FALLBACK}>
                <Suspense fallback={null}>
                    <ProfilesAnnouncementBackgroundYango />
                </Suspense>
            </ErrorBoundary>
        </section>
    );
};
